import { Col, Flex, Grid, TextInput, Title } from '@tremor/react';
import { z } from 'zod';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod/dist/zod';
import { EntityType } from '@/types/Organization';
import { useFetcherWithUtils } from '@/hooks/useFetcherWithUtils';
import { useGetPermissionsLevels } from '@/utils/helpers';
import { PermissionType } from '@/types/auth';
import { OutlinedButton } from '../layouts/Buttons/OutlinedButton/OutlinedButton';

const keywordNameSchema = z.object({
  name: z.string().min(3),
});

type KeywordName = z.infer<typeof keywordNameSchema>;

interface Props {
  name: string;
  entityType?: EntityType;
}

export const EntityDetails: FC<Props> = ({ name, entityType = 'workspaces' }) => {
  const { register, handleSubmit } = useForm<KeywordName>({
    resolver: zodResolver(keywordNameSchema),
    defaultValues: { name: name },
  });
  const [fetcher, isLoading] = useFetcherWithUtils();
  const [, , isAdmin] = useGetPermissionsLevels(
    entityType === 'workspaces' ? PermissionType.Workspace : PermissionType.Organization,
  );

  const onSubmit = (data: KeywordName) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('type', 'updateEntityName');
    fetcher.submit(formData, { method: 'put' });
  };

  const placeholder =
    entityType === 'workspaces' ? 'Workspace Name' : 'Organization Name';

  return (
    <Grid
      numCols={4}
      className="w-full grid-rows-[repeat(auto-fit,100%)] gap-1 mt-10 shadow-none rounded-xl border-gray-200 border"
    >
      <Col numColSpan={4} className="items-center bg-white w-full p-5 rounded-xl ">
        <Title className="text-2xl font-bold mb-3">{placeholder}</Title>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex justifyContent="start">
            <TextInput
              {...register('name')}
              className="py-0 px-2.5 w-[400px] h-8 border-[#0F0F19] rounded-xl shadow-none font-bold text-[#212121] [&_input]:pl-0"
              placeholder={placeholder}
              disabled={isLoading || !isAdmin}
            />
            <OutlinedButton
              isLoading={isLoading}
              disabled={!isAdmin}
              type="submit"
              className="ml-4"
            >
              Save
            </OutlinedButton>
          </Flex>
        </form>
      </Col>
    </Grid>
  );
};
